'use client';

import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Spinner } from '@/src/components/ui/Spinner';
import Image from 'next/image';

type Item = {
  id: number;
  uniqueLink: string;
  fullName?: string;
  avatarUrl?: string;
};

export type MentionListRef = {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
};

export const MentionList = forwardRef<
  MentionListRef,
  {
    items: Item[];
    command: (item: Item) => void;
    isLoading: boolean;
  }
>(function MentionList({ items, command, isLoading }, ref) {
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => setSelectedIndex(0), [items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowDown') {
        setSelectedIndex((i) => (i + 1) % Math.max(items.length, 1));
        return true;
      }
      if (event.key === 'ArrowUp') {
        setSelectedIndex(
          (i) => (i - 1 + items.length) % Math.max(items.length, 1),
        );
        return true;
      }
      if (event.key === 'Enter') {
        const item = items[selectedIndex];
        if (item) command(item);
        return true;
      }
      return false;
    },
  }));

  return (
    <div className="rounded-xl border bg-white shadow-md p-1 min-w-[200px]">
      {isLoading && (
        <div className="flex justify-center gap-2 px-2 py-2 text-sm opacity-70 w-full">
          <Spinner />
        </div>
      )}
      {!isLoading && items.length === 0 && (
        <div className="px-2 py-2 text-sm opacity-70">No results</div>
      )}
      {!isLoading &&
        items.map((item, idx) => (
          <button
            key={item.id}
            type="button"
            onClick={() => command(item)}
            className={[
              'w-full text-left px-3 py-1 text-sm rounded-lg flex items-center gap-2',
              idx === selectedIndex ? 'bg-gray-100' : 'hover:bg-gray-50',
            ].join(' ')}
          >
            <Image
              src={item.avatarUrl ?? '/images/default-avatar.png'}
              alt={item.fullName ?? item.uniqueLink}
              width={24}
              height={24}
              className="rounded-full"
            />
            <div className="flex flex-col">
              <div className="font-medium text-pink-accent">
                @{item.uniqueLink}
              </div>
              {!!item.fullName && (
                <div className="text-xs text-black">{item.fullName}</div>
              )}
            </div>
          </button>
        ))}
    </div>
  );
});
